import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  Card,
  Skeleton,
  Badge,
  Group,
  Alert,
  Button,
  TextInput,
  ActionIcon,
  Box,
  Anchor,
  Divider,
  Tabs,
  SimpleGrid,
  ThemeIcon,
  Paper,
  ScrollArea,
  Tooltip,
  TypographyStylesProvider,
} from '@mantine/core';
import {
  IconSparkles,
  IconSearch,
  IconAlertTriangle,
  IconArrowRight,
  IconRefresh,
  IconBook,
  IconBookmarks,
  IconLock,
  IconMessageCircle,
  IconSend,
  IconTrash,
  IconRobot,
  IconUser,
} from '@tabler/icons-react';
import {
  useKnowledgeBaseQAService,
  usePublicCoursesService,
} from '@actschurch/ccms-sdk';
import type {
  KnowledgeSearchResponse,
  KnowledgeSearchCitation,
  KnowledgeChatMessage,
  KnowledgeChatResponse,
  CourseModelView,
} from '@actschurch/ccms-sdk';
import { CourseCard } from '@/components/CourseCard';
import { useOptionalAuth } from '@/hooks/useOptionalAuth';
import { getAnonymousId } from '@/utils/anonymousId';

type Mode = 'catalog' | 'ai' | 'chat';

/**
 * Unified search page.
 *
 *  - Default tab is the **catalog** search — everyone (anonymous + signed-in)
 *    can filter the public course list locally by name / description /
 *    ministry name. No backend roundtrip per keystroke.
 *  - The **Ask AI** tab is sign-in gated. Anonymous viewers see a sign-in
 *    CTA instead of the AI form. Signed-in viewers get the full
 *    KnowledgeBaseQAService experience with citations.
 *
 * URL keeps the active tab + query so links round-trip correctly:
 *   <c>/search?q=faith</c>          → catalog tab
 *   <c>/search?q=faith&mode=ai</c>  → AI tab (auth required)
 */
export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') ?? '';
  const urlMode = searchParams.get('mode');
  const initialMode: Mode =
    urlMode === 'ai' ? 'ai' : urlMode === 'chat' ? 'chat' : 'catalog';

  const [q, setQ] = useState(initialQuery);
  const [mode, setMode] = useState<Mode>(initialMode);

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useOptionalAuth();

  // ─── Catalog data (cached for the session) ────────────────────────────────
  const publicCoursesService = usePublicCoursesService();
  const { data: catalog = [], isLoading: catalogLoading } = useQuery<CourseModelView[]>({
    queryKey: ['public', 'courses', 'view', 'search'],
    queryFn: () => publicCoursesService.listView(),
  });

  // ─── AI ───────────────────────────────────────────────────────────────────
  const qaService = useKnowledgeBaseQAService();
  const search = useMutation({
    mutationFn: (query: string) =>
      qaService.search({
        query,
        anonymousId: getAnonymousId(),
        maxResults: 6,
        includeCourses: true,
      }),
  });

  // Whether the Ask AI / Chat tabs are enabled (admin-managed in ccms_v2).
  // Defaults to visible so a transient availability error never hides a working
  // feature; once resolved, a disabled feature hides its tab.
  const { data: availability } = useQuery({
    queryKey: ['kb', 'ai-availability'],
    queryFn: () => qaService.getAvailability(),
    staleTime: 5 * 60 * 1000,
  });
  const aiSearchEnabled = availability?.searchEnabled ?? true;
  const aiChatEnabled = availability?.chatEnabled ?? true;
  // If the active tab has been disabled by an admin, fall back to the catalog so
  // the Tabs control never points at a tab that isn't rendered.
  const effectiveMode: Mode =
    (mode === 'ai' && !aiSearchEnabled) || (mode === 'chat' && !aiChatEnabled) ? 'catalog' : mode;

  // Sync URL → state when the user lands or pastes a link.
  useEffect(() => {
    const fromUrl = searchParams.get('q') ?? '';
    const urlModeNow = searchParams.get('mode');
    const modeFromUrl: Mode =
      urlModeNow === 'ai' ? 'ai' : urlModeNow === 'chat' ? 'chat' : 'catalog';
    if (fromUrl && fromUrl !== q) setQ(fromUrl);
    if (modeFromUrl !== mode) setMode(modeFromUrl);
    if (modeFromUrl === 'ai' && fromUrl && isAuthenticated && !search.data && !search.isPending) {
      search.mutate(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAuthenticated]);

  const writeUrl = (nextQuery: string, nextMode: Mode) => {
    const params: Record<string, string> = {};
    if (nextQuery) params.q = nextQuery;
    if (nextMode !== 'catalog') params.mode = nextMode;
    setSearchParams(params);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    writeUrl(query, mode);
    if (mode === 'ai' && isAuthenticated) search.mutate(query);
  };

  const onTabChange = (next: string | null) => {
    const m: Mode = next === 'ai' ? 'ai' : next === 'chat' ? 'chat' : 'catalog';
    setMode(m);
    writeUrl(q.trim(), m);
    if (m === 'ai' && q.trim() && isAuthenticated && !search.isPending) {
      search.mutate(q.trim());
    }
  };

  // ─── Catalog filter (memoised) ────────────────────────────────────────────
  const filteredCourses = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return catalog;
    return catalog.filter((c) => {
      const fields = [
        c.name,
        c.description,
        c.categoryName,
        c.ministryName,
      ]
        .filter(Boolean)
        .map((s) => stripTags(String(s)).toLowerCase());
      return fields.some((f) => f.includes(needle));
    });
  }, [catalog, q]);

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={1}>Search</Title>
        <Text c="dimmed">
          Find a course, lesson or ministry — or ask the AI assistant for an
          answer with citations.
        </Text>
      </Stack>

      <form onSubmit={handleSubmit}>
        <Group gap="xs" align="center" wrap="nowrap">
          <TextInput
            size="lg"
            radius="xl"
            leftSection={mode === 'ai' ? <IconSparkles size={18} /> : <IconSearch size={18} />}
            rightSection={
              <ActionIcon type="submit" variant="filled" radius="xl" size="lg" color="brand" aria-label="Search">
                <IconSearch size={16} />
              </ActionIcon>
            }
            placeholder={
              mode === 'ai'
                ? 'Ask anything — e.g. how do I set up sound on Sunday?'
                : 'Filter by course name, description or ministry…'
            }
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 0 }}
            aria-label="Search query"
            autoFocus
          />
        </Group>
      </form>

      <Tabs value={effectiveMode} onChange={onTabChange} variant="default">
        <Tabs.List>
          <Tabs.Tab value="catalog" leftSection={<IconBook size={14} />}>
            Catalog
            {q.trim() && (
              <Badge ml={6} size="xs" variant="light" color="gray">
                {filteredCourses.length}
              </Badge>
            )}
          </Tabs.Tab>
          {aiSearchEnabled && (
            <Tabs.Tab
              value="ai"
              leftSection={isAuthenticated ? <IconSparkles size={14} /> : <IconLock size={14} />}
            >
              Ask AI
            </Tabs.Tab>
          )}
          {aiChatEnabled && (
            <Tabs.Tab
              value="chat"
              leftSection={isAuthenticated ? <IconMessageCircle size={14} /> : <IconLock size={14} />}
            >
              Chat
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="catalog" pt="md">
          <CatalogResults
            query={q.trim()}
            isLoading={catalogLoading}
            results={filteredCourses}
          />
        </Tabs.Panel>

        {aiSearchEnabled && (
          <Tabs.Panel value="ai" pt="md">
            {!isAuthenticated ? (
              <SignInToAskCard kind="ai" location={location} />
            ) : (
              <AIResults search={search} q={q.trim()} onCitationOpen={(c) => c.deepLink && navigate(c.deepLink)} />
            )}
          </Tabs.Panel>
        )}

        {aiChatEnabled && (
          <Tabs.Panel value="chat" pt="md">
            {!isAuthenticated ? (
              <SignInToAskCard kind="chat" location={location} />
            ) : (
              <ChatPanel onCitationOpen={(c) => c.deepLink && navigate(c.deepLink)} />
            )}
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Catalog results
// ─────────────────────────────────────────────────────────────────────────────

function CatalogResults({
  query,
  isLoading,
  results,
}: {
  query: string;
  isLoading: boolean;
  results: CourseModelView[];
}) {
  if (isLoading) {
    return (
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} height={280} radius="lg" />
        ))}
      </SimpleGrid>
    );
  }

  if (!query) {
    return (
      <Stack gap="md">
        <Text c="dimmed" size="sm">
          {results.length} course{results.length === 1 ? '' : 's'} in the public catalog. Type
          to filter by name, description or ministry.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {results.slice(0, 9).map((course) => (
            <CourseCard
              key={course.courseID}
              course={course}
              categoryName={course.categoryName}
              categoryColor={course.categoryColor}
              ministryName={course.ministryName}
            />
          ))}
        </SimpleGrid>
      </Stack>
    );
  }

  if (results.length === 0) {
    return (
      <Card withBorder padding="xl" radius="lg">
        <Stack align="center" gap="sm">
          <ThemeIcon size={48} radius="xl" variant="light" color="gray">
            <IconBookmarks size={24} />
          </ThemeIcon>
          <Text c="dimmed">No courses matched "{query}". Try a different keyword or check the Knowledge Base.</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
      {results.map((course) => (
        <CourseCard
          key={course.courseID}
          course={course}
          categoryName={course.categoryName}
          categoryColor={course.categoryColor}
          ministryName={course.ministryName}
        />
      ))}
    </SimpleGrid>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  AI results — extracted so the catalog renders without touching the AI hook.
// ─────────────────────────────────────────────────────────────────────────────

function AIResults({
  search,
  q,
  onCitationOpen,
}: {
  search: ReturnType<typeof useMutation<KnowledgeSearchResponse | null, Error, string>>;
  q: string;
  onCitationOpen: (c: KnowledgeSearchCitation) => void;
}) {
  const result = search.data;
  const isRateLimited = result?.rateLimited === true;

  if (!q && !search.isPending && !result && !search.isError) {
    return (
      <Text c="dimmed" size="sm">
        Type a question above and press <b>Ask</b>. Try{' '}
        <Anchor component="button" type="button" onClick={() => search.mutate('how do I set up sound on Sunday')}>
          sound setup
        </Anchor>{' '}
        or{' '}
        <Anchor component="button" type="button" onClick={() => search.mutate('volunteer onboarding')}>
          volunteer onboarding
        </Anchor>.
      </Text>
    );
  }

  return (
    <Stack gap="md">
      {search.isPending && <ResultsSkeleton />}
      {search.isError && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
          We couldn't reach the AI service. Try again in a moment.
          <Group mt="xs">
            <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={() => search.mutate(q)}>
              Retry
            </Button>
          </Group>
        </Alert>
      )}
      {isRateLimited && result && (
        <Alert icon={<IconAlertTriangle size={16} />} color="orange" variant="light" title="Slow down a touch">
          {result.answer}
        </Alert>
      )}
      {result && !isRateLimited && (
        <ResultsPanel result={result} onCitationOpen={onCitationOpen} />
      )}
    </Stack>
  );
}

function ResultsSkeleton() {
  return (
    <Stack gap="md">
      <Skeleton height={120} radius="lg" />
      <Stack gap="xs">
        <Skeleton height={70} radius="md" />
        <Skeleton height={70} radius="md" />
        <Skeleton height={70} radius="md" />
      </Stack>
    </Stack>
  );
}

function ResultsPanel({
  result,
  onCitationOpen,
}: {
  result: KnowledgeSearchResponse;
  onCitationOpen: (c: KnowledgeSearchCitation) => void;
}) {
  // Dedupe citations by entityType+entityId so the same article doesn't
  // appear 3x with the same score when lexical retrieval picks up multiple
  // chunks of the same row.
  const uniqueCitations = useMemo(() => {
    const seen = new Set<string>();
    const out: KnowledgeSearchCitation[] = [];
    for (const c of result.citations) {
      const key = `${c.entityType}:${c.entityId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  }, [result.citations]);

  return (
    <Stack gap="md">
      <Card withBorder padding="lg" radius="lg" shadow="sm">
        <Group gap="xs" mb="xs">
          <IconSparkles size={16} color="var(--mantine-color-brand-6)" />
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Answer
          </Text>
          <Badge size="xs" variant="light" color="gray">
            {result.source}
          </Badge>
        </Group>
        <TypographyStylesProvider style={{ fontSize: 15 }}>
          <div dangerouslySetInnerHTML={{ __html: markdownToHtml(result.answer) }} />
        </TypographyStylesProvider>
      </Card>

      {uniqueCitations.length > 0 && (
        <>
          <Divider label="Sources" labelPosition="center" />
          <Stack gap="xs">
            {uniqueCitations.map((c, idx) => (
              <CitationRow key={`${c.entityType}-${c.entityId}`} citation={c} index={idx + 1} onOpen={onCitationOpen} />
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}

function CitationRow({
  citation,
  index,
  onOpen,
}: {
  citation: KnowledgeSearchCitation;
  index: number;
  onOpen: (c: KnowledgeSearchCitation) => void;
}) {
  const labelByEntity: Record<string, string> = {
    KBArticle: 'KB article',
    Course: 'Course',
    CourseSession: 'Session',
  };
  const color =
    citation.entityType === 'KBArticle' ? 'teal' :
    citation.entityType === 'Course' ? 'grape' :
    citation.entityType === 'CourseSession' ? 'blue' : 'gray';

  const content = (
    <Group justify="space-between" wrap="nowrap" align="flex-start">
      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs">
          <Badge variant="light" color={color}>
            {index}. {labelByEntity[citation.entityType] ?? citation.entityType}
          </Badge>
          {citation.confidence > 0 && (
            <Badge variant="outline" size="sm" color="gray">
              score {Number(citation.confidence).toFixed(2)}
            </Badge>
          )}
        </Group>
        <Text fw={600} lineClamp={1}>{citation.title}</Text>
        {citation.snippet && (
          <Text size="sm" c="dimmed" lineClamp={2}>
            {citation.snippet}
          </Text>
        )}
      </Stack>
      {/* Only advertise an "open" affordance when the citation is actually
          navigable. A dead arrow on a non-clickable card reads as broken. */}
      {citation.deepLink && (
        <Box>
          <IconArrowRight size={16} />
        </Box>
      )}
    </Group>
  );

  if (citation.deepLink) {
    return (
      <Card
        component={Link}
        to={citation.deepLink}
        withBorder
        padding="md"
        radius="md"
        style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
        onClick={() => onOpen(citation)}
      >
        {content}
      </Card>
    );
  }

  return (
    <Card withBorder padding="md" radius="md">
      {content}
      <Text size="xs" c="dimmed" mt={6}>
        This source isn't published as a public page yet — search its title in the catalog or knowledge base to open it.
      </Text>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Sign-in gate (shared by Ask AI + Chat)
// ─────────────────────────────────────────────────────────────────────────────

function SignInToAskCard({
  kind,
  location,
}: {
  kind: 'ai' | 'chat';
  location: { pathname: string; search: string };
}) {
  const title = kind === 'ai' ? 'Sign in to ask the AI' : 'Sign in to start a chat';
  return (
    <Card withBorder padding="xl" radius="lg">
      <Stack align="center" gap="sm" py="md">
        <ThemeIcon size={56} radius="xl" variant="light" color="brand">
          {kind === 'ai' ? <IconSparkles size={28} /> : <IconMessageCircle size={28} />}
        </ThemeIcon>
        <Title order={3} ta="center">{title}</Title>
        <Text c="dimmed" ta="center" maw={520}>
          The AI assistant pulls from member-only knowledge as well as public
          content, so it's reserved for signed-in volunteers and leaders. The
          catalog tab still works without an account.
        </Text>
        <Button
          component={Link}
          to={`/auth/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
          size="md"
          leftSection={<IconLock size={16} />}
        >
          Sign in to continue
        </Button>
      </Stack>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Chat panel — multi-turn conversation persisted to localStorage.
// ─────────────────────────────────────────────────────────────────────────────

const CHAT_STORAGE_KEY = 'ccms:kb-chat:v1';
const MAX_TRANSCRIPT_TURNS = 30; // hard cap so token costs + storage stay bounded

interface ChatTurn extends KnowledgeChatMessage {
  /** Local id so React keys are stable when transcripts grow. */
  id: string;
  /** Optional citations attached to an assistant answer. */
  citations?: KnowledgeSearchCitation[];
  /** "answer" | "clarification" — chosen by the backend, used for styling. */
  kind?: 'answer' | 'clarification' | 'rate-limited' | 'empty-index';
}

function loadChatHistory(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatTurn[];
    return Array.isArray(parsed) ? parsed.slice(-MAX_TRANSCRIPT_TURNS) : [];
  } catch {
    return [];
  }
}

function saveChatHistory(turns: ChatTurn[]) {
  try {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(turns.slice(-MAX_TRANSCRIPT_TURNS)));
  } catch {
    /* storage full / disabled — ignore */
  }
}

function newTurnId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function ChatPanel({ onCitationOpen }: { onCitationOpen: (c: KnowledgeSearchCitation) => void }) {
  const qaService = useKnowledgeBaseQAService();
  const [turns, setTurns] = useState<ChatTurn[]>(() => loadChatHistory());
  const [draft, setDraft] = useState('');
  const viewportRef = useRef<HTMLDivElement | null>(null);

  // Persist transcript on every change so a refresh / new tab keeps the
  // same conversation. Capped at MAX_TRANSCRIPT_TURNS to keep both
  // localStorage and outbound payloads bounded.
  useEffect(() => {
    saveChatHistory(turns);
  }, [turns]);

  // Auto-scroll to the latest message whenever the transcript grows.
  useEffect(() => {
    const el = viewportRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [turns.length]);

  const chat = useMutation<KnowledgeChatResponse | null, Error, ChatTurn[]>({
    mutationFn: (transcript) =>
      qaService.chat({
        messages: transcript.map((t) => ({ role: t.role, content: t.content })),
        anonymousId: getAnonymousId(),
        maxResults: 6,
        includeCourses: true,
      }),
    onSuccess: (resp) => {
      if (!resp) return;
      setTurns((prev) => [
        ...prev,
        {
          id: newTurnId(),
          role: 'assistant',
          content: resp.content,
          citations: resp.citations,
          kind: resp.kind,
        },
      ]);
    },
    onError: (err) => {
      setTurns((prev) => [
        ...prev,
        {
          id: newTurnId(),
          role: 'assistant',
          content: `Something went wrong: ${err.message}. Please try again.`,
          kind: 'answer',
        },
      ]);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || chat.isPending) return;
    const userTurn: ChatTurn = { id: newTurnId(), role: 'user', content: text };
    const next = [...turns, userTurn].slice(-MAX_TRANSCRIPT_TURNS);
    setTurns(next);
    setDraft('');
    chat.mutate(next);
  };

  const clearChat = () => {
    setTurns([]);
    try { localStorage.removeItem(CHAT_STORAGE_KEY); } catch { /* noop */ }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap" gap="xs">
        <Text size="sm" c="dimmed">
          Chat with the assistant. It can ask clarifying questions to find the right article for you.
          Conversation history stays on this device.
        </Text>
        {turns.length > 0 && (
          <Tooltip label="Start a new conversation">
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={<IconTrash size={14} />}
              onClick={clearChat}
            >
              Clear
            </Button>
          </Tooltip>
        )}
      </Group>

      <Paper withBorder radius="lg" p="md" style={{ background: 'var(--mantine-color-default-hover)' }}>
        <ScrollArea h={420} viewportRef={viewportRef} type="auto">
          <Stack gap="sm" pr="xs">
            {turns.length === 0 && (
              <ChatStarter onPick={(seed) => {
                const userTurn: ChatTurn = { id: newTurnId(), role: 'user', content: seed };
                setTurns([userTurn]);
                chat.mutate([userTurn]);
              }} />
            )}
            {turns.map((t) => (
              <ChatBubble key={t.id} turn={t} onCitationOpen={onCitationOpen} />
            ))}
            {chat.isPending && (
              <Group gap="xs" align="flex-start" wrap="nowrap">
                <ThemeIcon size={28} radius="xl" variant="light" color="brand">
                  <IconRobot size={14} />
                </ThemeIcon>
                <Stack gap={4} style={{ flex: 1 }}>
                  <Skeleton height={14} width="60%" />
                  <Skeleton height={14} width="80%" />
                </Stack>
              </Group>
            )}
          </Stack>
        </ScrollArea>
      </Paper>

      <form onSubmit={submit}>
        <Group gap="xs" align="flex-end" wrap="nowrap">
          <TextInput
            placeholder="Ask a follow-up… (Enter to send)"
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            radius="xl"
            size="md"
            style={{ flex: 1 }}
            disabled={chat.isPending}
            autoFocus
          />
          <ActionIcon
            type="submit"
            radius="xl"
            size={42}
            variant="filled"
            color="brand"
            disabled={!draft.trim() || chat.isPending}
            aria-label="Send message"
          >
            <IconSend size={18} />
          </ActionIcon>
        </Group>
      </form>
    </Stack>
  );
}

function ChatStarter({ onPick }: { onPick: (seed: string) => void }) {
  const suggestions = [
    'How do I set up the sound desk on a Sunday?',
    'What does volunteer onboarding involve?',
    'Where do I find the Planning Centre integration guide?',
  ];
  return (
    <Stack gap="sm" align="center" ta="center" py="lg">
      <ThemeIcon size={48} radius="xl" variant="light" color="brand">
        <IconMessageCircle size={24} />
      </ThemeIcon>
      <Text fw={600}>Start a conversation</Text>
      <Text size="sm" c="dimmed" maw={420}>
        Ask anything about courses, the knowledge base or ministry workflows. If your question is
        ambiguous I'll ask a follow-up question instead of guessing.
      </Text>
      <Stack gap={6} mt="xs">
        {suggestions.map((s) => (
          <Anchor key={s} component="button" type="button" size="sm" onClick={() => onPick(s)}>
            {s}
          </Anchor>
        ))}
      </Stack>
    </Stack>
  );
}

function ChatBubble({
  turn,
  onCitationOpen,
}: {
  turn: ChatTurn;
  onCitationOpen: (c: KnowledgeSearchCitation) => void;
}) {
  const isUser = turn.role === 'user';
  const isClarification = turn.kind === 'clarification';

  // Lexical retrieval often returns the same source row multiple times
  // (one per chunk / step), and the LLM prompt happily echoes all of
  // them. Dedupe by entityType+entityId so the citations panel doesn't
  // show "Part 1: Preparing Your Laptop" three times with identical
  // scores. Keep the first occurrence (= highest-ranked).
  const uniqueCitations = useMemo(() => {
    if (!turn.citations || turn.citations.length === 0) return [];
    const seen = new Set<string>();
    const out: KnowledgeSearchCitation[] = [];
    for (const c of turn.citations) {
      const key = `${c.entityType}:${c.entityId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  }, [turn.citations]);

  return (
    <Group gap="xs" align="flex-start" wrap="nowrap" justify={isUser ? 'flex-end' : 'flex-start'}>
      {!isUser && (
        <ThemeIcon size={28} radius="xl" variant="light" color={isClarification ? 'orange' : 'brand'}>
          <IconRobot size={14} />
        </ThemeIcon>
      )}
      <Stack gap={4} style={{ maxWidth: '85%' }} align={isUser ? 'flex-end' : 'flex-start'}>
        <Paper
          withBorder
          radius="lg"
          p="sm"
          style={{
            background: isUser
              ? 'var(--mantine-color-brand-light)'
              : 'var(--mantine-color-body)',
          }}
        >
          {isClarification && (
            <Badge color="orange" size="xs" variant="light" mb={4}>
              Quick clarification
            </Badge>
          )}
          {isUser ? (
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{turn.content}</Text>
          ) : (
            // Assistant replies often contain markdown (**bold**, lists,
            // links, code). Convert to HTML and render through Mantine's
            // TypographyStylesProvider so formatting matches the rest of
            // the resource centre.
            <TypographyStylesProvider style={{ fontSize: 14 }}>
              <div dangerouslySetInnerHTML={{ __html: markdownToHtml(turn.content) }} />
            </TypographyStylesProvider>
          )}
        </Paper>

        {uniqueCitations.length > 0 && (
          <Stack gap={4} w="100%">
            {uniqueCitations.map((c, idx) => (
              <CitationRow
                key={`${c.entityType}-${c.entityId}`}
                citation={c}
                index={idx + 1}
                onOpen={onCitationOpen}
              />
            ))}
          </Stack>
        )}
      </Stack>
      {isUser && (
        <ThemeIcon size={28} radius="xl" variant="light" color="gray">
          <IconUser size={14} />
        </ThemeIcon>
      )}
    </Group>
  );
}

/**
 * Minimal markdown → HTML converter for the subset Claude tends to emit:
 *   **bold**, *italic*, `code`, [text](url), ordered / unordered lists,
 *   paragraphs separated by blank lines.
 *
 * Avoids pulling a full markdown library for this single use site. HTML
 * is rendered inside <TypographyStylesProvider>; we always escape raw
 * &lt;&gt;&amp; first so model output can't inject arbitrary markup, then
 * re-introduce the small set of tags we trust.
 */
function markdownToHtml(md: string): string {
  if (!md) return '';
  // Escape HTML first so anything the model emits literally stays literal.
  const escape = (s: string) =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  let src = escape(md);

  // Code spans first so their contents aren't reinterpreted as emphasis.
  src = src.replace(/`([^`]+)`/g, (_, code) => `<code>${code}</code>`);

  // Links: [text](url)
  src = src.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, href) => {
    const safeHref = /^(https?:|\/|mailto:)/i.test(href) ? href : '#';
    return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });

  // Bold + italic (handle **bold** before *italic*).
  src = src.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  src = src.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>');

  // Build block-level structure paragraph-by-paragraph so lists wrap properly.
  const blocks = src.split(/\n{2,}/);
  const html = blocks
    .map((block) => {
      const lines = block.split('\n');
      const allBulleted = lines.every((l) => /^\s*[-*]\s+/.test(l));
      const allOrdered = lines.every((l) => /^\s*\d+\.\s+/.test(l));
      if (allBulleted && lines.length > 0) {
        const items = lines.map((l) => `<li>${l.replace(/^\s*[-*]\s+/, '')}</li>`).join('');
        return `<ul>${items}</ul>`;
      }
      if (allOrdered && lines.length > 0) {
        const items = lines.map((l) => `<li>${l.replace(/^\s*\d+\.\s+/, '')}</li>`).join('');
        return `<ol>${items}</ol>`;
      }
      return `<p>${block.replace(/\n/g, '<br />')}</p>`;
    })
    .join('');

  return html;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────

function stripTags(html: string): string {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent ?? '';
  } catch {
    return html;
  }
}
